import React, { useState } from 'react';
import { AdminService } from '../../services/adminService';
import { isDevelopment } from '../../config/constants';
import { useDialog } from '../../context/DialogContext';
import styles from '../../styles/admin/panelEditor.module.css';

interface Panel {
  id: string;
  title: string;
  subtext: string;
  format: 'list_with_images' | 'list_links' | 'list_text' | 'list_videos';
  order: number;
  items: any[];
  icon_url?: string;
  accent_color?: string;
  show_item_count?: boolean;
}

interface PanelEditorProps {
  panels: Panel[];
  programId: string;
  onChange: (panels: Panel[]) => void;
}

const PanelEditor: React.FC<PanelEditorProps> = ({ panels, programId, onChange }) => {
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingPanelIcon, setUploadingPanelIcon] = useState(false);
  const fileInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});
  const panelIconInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});
  const { confirmAsync, showAlert } = useDialog();
  const showError = (message: string) => showAlert({ title: 'Something went wrong', message, okText: 'OK', variant: 'danger' });

  const handleAddPanel = () => {
    if (panels.length >= 9) {
      showAlert({ title: 'Panel limit reached', message: 'A tab can have at most 9 panels.', okText: 'OK', variant: 'warning' });
      return;
    }

    const newPanel: Panel = {
      id: `panel-${Date.now()}`,
      title: 'New Panel',
      subtext: '',
      format: 'list_links',
      order: panels.length + 1,
      items: [],
      show_item_count: true
    };

    onChange([...panels, newPanel]);
    setExpandedPanel(newPanel.id);
  };

  const handleRemovePanel = async (panelId: string) => {
    const ok = await confirmAsync({
      title: 'Remove panel?',
      message: 'This removes the panel and all its content.',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    
    const updatedPanels = panels
      .filter(p => p.id !== panelId)
      .map((p, index) => ({ ...p, order: index + 1 }));
    
    onChange(updatedPanels);
    if (expandedPanel === panelId) {
      setExpandedPanel(null);
    }
  };

  const handleMovePanel = (panelId: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
    const index = panels.findIndex(p => p.id === panelId);
    if (index === -1) return;

    const sortedPanels = [...panels].sort((a, b) => a.order - b.order);
    const currentIndex = sortedPanels.findIndex(p => p.id === panelId);
    
    let newIndex = currentIndex;
    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < sortedPanels.length - 1) {
      newIndex = currentIndex + 1;
    } else if (direction === 'top') {
      newIndex = 0;
    } else if (direction === 'bottom') {
      newIndex = sortedPanels.length - 1;
    }

    if (newIndex !== currentIndex) {
      const [removed] = sortedPanels.splice(currentIndex, 1);
      sortedPanels.splice(newIndex, 0, removed);
      
      const reorderedPanels = sortedPanels.map((p, i) => ({ ...p, order: i + 1 }));
      onChange(reorderedPanels);
    }
  };

  const handlePanelChange = (panelId: string, field: string, value: any) => {
    const updatedPanels = panels.map(p => 
      p.id === panelId ? { ...p, [field]: value } : p
    );
    onChange(updatedPanels);
  };

  const handleAddItem = (panelId: string) => {
    const panel = panels.find(p => p.id === panelId);
    if (!panel) return;

    let newItem: any = {};
    
    switch (panel.format) {
      case 'list_with_images':
        newItem = { title: '', subtext: '', image_url: '', link_type: 'external', link_value: '' };
        break;
      case 'list_links':
        newItem = { title: '', subtext: '', link_type: 'external', link_value: '' };
        break;
      case 'list_text':
        newItem = { text: '' };
        break;
      case 'list_videos':
        newItem = { title: '', subtext: '', youtube_url: '' };
        break;
    }

    const updatedPanels = panels.map(p => 
      p.id === panelId ? { ...p, items: [...p.items, newItem] } : p
    );
    onChange(updatedPanels);
  };

  const handleRemoveItem = async (panelId: string, itemIndex: number) => {
    const panel = panels.find(p => p.id === panelId);
    if (!panel) return;
    
    const item = panel.items[itemIndex];
    const itemName = panel.format === 'list_text'
      ? (item.text?.substring(0, 40) || 'this item')
      : (item.title || 'this item');
    
    const ok = await confirmAsync({
      title: 'Remove item?',
      message: `Remove "${itemName}${itemName.length > 40 ? '...' : ''}"?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    
    const updatedPanels = panels.map(p => 
      p.id === panelId ? { ...p, items: p.items.filter((_, i) => i !== itemIndex) } : p
    );
    onChange(updatedPanels);
  };

  const handleItemChange = (panelId: string, itemIndex: number, field: string, value: any) => {
    if (isDevelopment()) console.log(`Setting ${field} to:`, value, `for panel ${panelId}, item ${itemIndex}`);
    const updatedPanels = panels.map(p => {
      if (p.id === panelId) {
        const updatedItems = p.items.map((item, i) => 
          i === itemIndex ? { ...item, [field]: value } : item
        );
        if (isDevelopment()) console.log('Updated item:', updatedItems[itemIndex]);
        return { ...p, items: updatedItems };
      }
      return p;
    });
    onChange(updatedPanels);
  };

  const toggleItemExpansion = (panelId: string, itemIndex: number) => {
    const itemKey = `${panelId}-${itemIndex}`;
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemKey)) {
      newExpanded.delete(itemKey);
    } else {
      newExpanded.add(itemKey);
    }
    setExpandedItems(newExpanded);
  };

  const handleMoveItem = (panelId: string, itemIndex: number, direction: 'up' | 'down' | 'top' | 'bottom') => {
    const panel = panels.find(p => p.id === panelId);
    if (!panel) return;

    let newIndex = itemIndex;
    if (direction === 'up' && itemIndex > 0) {
      newIndex = itemIndex - 1;
    } else if (direction === 'down' && itemIndex < panel.items.length - 1) {
      newIndex = itemIndex + 1;
    } else if (direction === 'top') {
      newIndex = 0;
    } else if (direction === 'bottom') {
      newIndex = panel.items.length - 1;
    }

    if (newIndex === itemIndex) return;

    const updatedPanels = panels.map(p => {
      if (p.id === panelId) {
        const newItems = [...p.items];
        const [removed] = newItems.splice(itemIndex, 1);
        newItems.splice(newIndex, 0, removed);
        return { ...p, items: newItems };
      }
      return p;
    });
    onChange(updatedPanels);
  };

  const handleImageUpload = async (panelId: string, itemIndex: number, file: File) => {
    try {
      setUploadingImage(true);
      const response = await AdminService.uploadProgramImage(file, programId);
      if (isDevelopment()) console.log('Upload response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from upload endpoint');
      }
      
      handleItemChange(panelId, itemIndex, 'image_url', response.url);
      
      // Reset file input
      const inputKey = `image-${panelId}-${itemIndex}`;
      if (fileInputRefs.current[inputKey]) {
        fileInputRefs.current[inputKey]!.value = '';
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      showError(`Failed to upload image: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePanelIconUpload = async (panelId: string, file: File) => {
    try {
      setUploadingPanelIcon(true);
      const response = await AdminService.uploadProgramImage(file, programId);
      if (isDevelopment()) console.log('Panel icon upload response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from upload endpoint');
      }
      
      handlePanelChange(panelId, 'icon_url', response.url);
      
      // Reset file input
      const inputKey = `panel-icon-${panelId}`;
      if (panelIconInputRefs.current[inputKey]) {
        panelIconInputRefs.current[inputKey]!.value = '';
      }
    } catch (err) {
      console.error('Error uploading panel icon:', err);
      showError(`Failed to upload icon: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingPanelIcon(false);
    }
  };

  const handleDocumentUpload = async (panelId: string, itemIndex: number, file: File) => {
    try {
      setUploadingDocument(true);
      const response = await AdminService.uploadProgramDocument(file, programId);
      if (isDevelopment()) console.log('Document upload response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from upload endpoint');
      }
      
      // Update both link_value and link_type in a single operation to avoid race condition
      const updatedPanels = panels.map(p => {
        if (p.id === panelId) {
          const updatedItems = p.items.map((item, i) => 
            i === itemIndex ? { ...item, link_value: response.url, link_type: 'document' } : item
          );
          return { ...p, items: updatedItems };
        }
        return p;
      });
      onChange(updatedPanels);
      
      // Reset file input
      const inputKey = `document-${panelId}-${itemIndex}`;
      if (fileInputRefs.current[inputKey]) {
        fileInputRefs.current[inputKey]!.value = '';
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      showError(`Failed to upload document: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingDocument(false);
    }
  };

  const getItemSummary = (item: any, format: string): string => {
    if (format === 'list_text') {
      return item.text ? item.text.substring(0, 60) + (item.text.length > 60 ? '...' : '') : 'Empty text';
    } else {
      return item.title || 'Untitled';
    }
  };

  // Since we're only editing a single panel passed from parent, just use the first one
  const panel = panels[0];
  
  React.useEffect(() => {
    if (isDevelopment() && panel && panel.items) {
      console.log('Panel items updated:', panel.items);
    }
  }, [panel?.items]);

  if (!panel) {
    return <div className={styles.emptyState}>No panel data available.</div>;
  }

  return (
    <div className={styles.panelEditorContent}>
                {/* Panel Settings */}
                <div className={styles.panelSettings}>
                  <div className={styles.formGroup}>
                    <label>Panel Title</label>
                    <input
                      type="text"
                      value={panel.title}
                      onChange={(e) => handlePanelChange(panel.id, 'title', e.target.value)}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Panel Subtext</label>
                    <input
                      type="text"
                      value={panel.subtext}
                      onChange={(e) => handlePanelChange(panel.id, 'subtext', e.target.value)}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Panel Format</label>
                    <select
                      value={panel.format}
                      onChange={(e) => handlePanelChange(panel.id, 'format', e.target.value)}
                      className={styles.select}
                    >
                      <option value="list_with_images">List with Images</option>
                      <option value="list_links">List of Links</option>
                      <option value="list_text">List of Text</option>
                      <option value="list_videos">List of Videos</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Panel Icon (optional)</label>
                    <div className={styles.imageUploadGroup}>
                      {panel.icon_url && (
                        <div className={styles.imagePreview}>
                          <img 
                            src={panel.icon_url} 
                            alt="Panel icon" 
                            className={styles.previewImage}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handlePanelChange(panel.id, 'icon_url', '')}
                            className={styles.removeImageButton}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <input
                        ref={(el) => {
                          const inputKey = `panel-icon-${panel.id}`;
                          panelIconInputRefs.current[inputKey] = el;
                        }}
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handlePanelIconUpload(panel.id, e.target.files[0]);
                          }
                        }}
                        disabled={uploadingPanelIcon}
                        className={styles.fileInput}
                      />
                      {uploadingPanelIcon && <span className={styles.uploading}>Uploading...</span>}
                      <p className={styles.helpText}>Upload a small icon (32-40px recommended) to display next to the panel title</p>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Accent Color (optional)</label>
                    <select
                      value={panel.accent_color || ''}
                      onChange={(e) => handlePanelChange(panel.id, 'accent_color', e.target.value || undefined)}
                      className={styles.select}
                    >
                      <option value="">Default (Green)</option>
                      <option value="#4CAF50">Green</option>
                      <option value="#2196F3">Blue</option>
                      <option value="#9C27B0">Purple</option>
                      <option value="#FF9800">Orange</option>
                      <option value="#F44336">Red</option>
                      <option value="#00BCD4">Cyan</option>
                      <option value="#8BC34A">Light Green</option>
                      <option value="#FF5722">Deep Orange</option>
                    </select>
                    <p className={styles.helpText}>Choose a color for the panel's left border accent</p>
                  </div>
                  <div className={styles.formGroup}>
                    <label>
                      <input
                        type="checkbox"
                        checked={panel.show_item_count !== false}
                        onChange={(e) => handlePanelChange(panel.id, 'show_item_count', e.target.checked)}
                      />
                      {' '}Show item count badge
                    </label>
                    <p className={styles.helpText}>Display the number of items in this panel</p>
                  </div>
                </div>

                {/* Panel Items */}
                <div className={styles.itemsSection}>
                  <div className={styles.itemsHeader}>
                    <h5>Panel Items</h5>
                    <button onClick={() => handleAddItem(panel.id)} className={styles.addItemButton}>
                      + Add Item
                    </button>
                  </div>

                  {panel.items.length === 0 ? (
                    <div className={styles.emptyItems}>No items yet. Click "Add Item" to get started.</div>
                  ) : (
                    <div className={styles.itemsList}>
                      {panel.items.map((item, itemIndex) => {
                        const itemKey = `${panel.id}-${itemIndex}`;
                        const isExpanded = expandedItems.has(itemKey);
                        const itemSummary = getItemSummary(item, panel.format);
                        
                        if (isDevelopment() && isExpanded && itemIndex === 0) {
                          console.log('Rendering item 0 (expanded):', item);
                          console.log('Item image_url:', item.image_url);
                        }

                        return (
                          <div key={itemIndex} className={styles.itemAccordion}>
                            <div className={styles.itemAccordionHeader} onClick={() => toggleItemExpansion(panel.id, itemIndex)}>
                              <div className={styles.itemInfo}>
                                <strong>Item {itemIndex + 1}:</strong>
                                <span className={styles.itemSummary}>{itemSummary}</span>
                              </div>
                              <div className={styles.itemActions} onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleMoveItem(panel.id, itemIndex, 'top')}
                                  disabled={itemIndex === 0}
                                  className={styles.moveButton}
                                  title="Move to top"
                                >
                                  ⏫
                                </button>
                                <button
                                  onClick={() => handleMoveItem(panel.id, itemIndex, 'up')}
                                  disabled={itemIndex === 0}
                                  className={styles.moveButton}
                                  title="Move up one"
                                >
                                  ⬆️
                                </button>
                                <button
                                  onClick={() => handleMoveItem(panel.id, itemIndex, 'down')}
                                  disabled={itemIndex === panel.items.length - 1}
                                  className={styles.moveButton}
                                  title="Move down one"
                                >
                                  ⬇️
                                </button>
                                <button
                                  onClick={() => handleMoveItem(panel.id, itemIndex, 'bottom')}
                                  disabled={itemIndex === panel.items.length - 1}
                                  className={styles.moveButton}
                                  title="Move to bottom"
                                >
                                  ⏬
                                </button>
                                <button
                                  onClick={() => void handleRemoveItem(panel.id, itemIndex)}
                                  className={styles.removeButton}
                                  title="Remove item"
                                >
                                  🗑️
                                </button>
                                <button className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
                                  ▼
                                </button>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className={styles.itemEditorBody}>
                                {/* List with Images Format */}
                                {panel.format === 'list_with_images' && (
                            <>
                              <div className={styles.formGroup}>
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={item.title || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'title', e.target.value)}
                                  className={styles.input}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Subtext</label>
                                <textarea
                                  value={item.subtext || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'subtext', e.target.value)}
                                  className={styles.textarea}
                                  rows={2}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Image</label>
                                <div className={styles.imageUploadGroup}>
                                  {item.image_url && (
                                    <div>
                                      <div style={{fontSize: '0.85rem', color: '#4CAF50', fontWeight: 500, marginBottom: '4px'}}>
                                        ✓ Image Uploaded
                                      </div>
                                      <div className={styles.imagePreview}>
                                        <img 
                                          src={item.image_url} 
                                          alt="Preview"
                                        />
                                      </div>
                                      <div style={{fontSize: '0.75rem', color: '#666', marginBottom: '8px', wordBreak: 'break-all'}}>
                                        {item.image_url}
                                      </div>
                                    </div>
                                  )}
                                  <input
                                    ref={(el) => {
                                      const inputKey = `image-${panel.id}-${itemIndex}`;
                                      fileInputRefs.current[inputKey] = el;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleImageUpload(panel.id, itemIndex, e.target.files[0]);
                                      }
                                    }}
                                    disabled={uploadingImage}
                                    className={styles.fileInput}
                                  />
                                  {uploadingImage && <span className={styles.uploading}>Uploading...</span>}
                                </div>
                              </div>
                              <div className={styles.formGroup}>
                                <label>Link Type</label>
                                <select
                                  value={item.link_type || 'external'}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'link_type', e.target.value)}
                                  className={styles.select}
                                >
                                  <option value="external">External URL</option>
                                  <option value="document">Uploaded Document</option>
                                  <option value="route">Internal Route</option>
                                </select>
                              </div>
                              {item.link_type === 'document' ? (
                                <div className={styles.formGroup}>
                                  <label>Upload Document (PDF)</label>
                                  <div className={styles.documentUploadGroup}>
                                    {item.link_value && (() => {
                                      // Extract filename from URL
                                      const urlParts = item.link_value.split('/');
                                      const filename = urlParts[urlParts.length - 1] || 'document.pdf';
                                      // Remove timestamp prefix if present (format: timestamp-filename)
                                      const displayName = filename.includes('-') ? filename.split('-').slice(1).join('-') : filename;
                                      
                                      // Fix old URL format (recoverycommons.com/... -> recoverycommons.com.s3.amazonaws.com/...)
                                      let documentUrl = item.link_value;
                                      if (documentUrl.startsWith('https://recoverycommons.com/') && !documentUrl.includes('.s3.amazonaws.com')) {
                                        documentUrl = documentUrl.replace('https://recoverycommons.com/', 'https://recoverycommons.com.s3.amazonaws.com/');
                                      }
                                      
                                      return (
                                        <div className={styles.documentInfo}>
                                          <span className={styles.documentName}>{displayName}</span>
                                          <a href={documentUrl} target="_blank" rel="noopener noreferrer" className={styles.viewLink}>
                                            View Current Document
                                          </a>
                                        </div>
                                      );
                                    })()}
                                    <input
                                      ref={(el) => {
                                        const inputKey = `document-${panel.id}-${itemIndex}`;
                                        fileInputRefs.current[inputKey] = el;
                                      }}
                                      type="file"
                                      accept=".pdf,.doc,.docx"
                                      onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                          handleDocumentUpload(panel.id, itemIndex, e.target.files[0]);
                                        }
                                      }}
                                      disabled={uploadingDocument}
                                      className={styles.fileInput}
                                    />
                                    {uploadingDocument && <span className={styles.uploading}>Uploading...</span>}
                                  </div>
                                </div>
                              ) : (
                                <div className={styles.formGroup}>
                                  <label>Link URL</label>
                                  <div className={styles.urlGroup}>
                                    <input
                                      type="text"
                                      value={item.link_value || ''}
                                      onChange={(e) => handleItemChange(panel.id, itemIndex, 'link_value', e.target.value)}
                                      className={styles.input}
                                      placeholder="https://..."
                                    />
                                    {item.link_value && (
                                      <a href={item.link_value} target="_blank" rel="noopener noreferrer" className={styles.testLink}>
                                        Test
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* List Links Format */}
                          {panel.format === 'list_links' && (
                            <>
                              <div className={styles.formGroup}>
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={item.title || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'title', e.target.value)}
                                  className={styles.input}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Subtext</label>
                                <textarea
                                  value={item.subtext || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'subtext', e.target.value)}
                                  className={styles.textarea}
                                  rows={2}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Link Type</label>
                                <select
                                  value={item.link_type || 'external'}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'link_type', e.target.value)}
                                  className={styles.select}
                                >
                                  <option value="external">External URL</option>
                                  <option value="document">Uploaded Document</option>
                                  <option value="route">Internal Route</option>
                                </select>
                              </div>
                              {item.link_type === 'document' ? (
                                <div className={styles.formGroup}>
                                  <label>Upload Document (PDF)</label>
                                  <div className={styles.documentUploadGroup}>
                                    {item.link_value && (() => {
                                      // Extract filename from URL
                                      const urlParts = item.link_value.split('/');
                                      const filename = urlParts[urlParts.length - 1] || 'document.pdf';
                                      // Remove timestamp prefix if present (format: timestamp-filename)
                                      const displayName = filename.includes('-') ? filename.split('-').slice(1).join('-') : filename;
                                      
                                      // Fix old URL format (recoverycommons.com/... -> recoverycommons.com.s3.amazonaws.com/...)
                                      let documentUrl = item.link_value;
                                      if (documentUrl.startsWith('https://recoverycommons.com/') && !documentUrl.includes('.s3.amazonaws.com')) {
                                        documentUrl = documentUrl.replace('https://recoverycommons.com/', 'https://recoverycommons.com.s3.amazonaws.com/');
                                      }
                                      
                                      return (
                                        <div className={styles.documentInfo}>
                                          <span className={styles.documentName}>{displayName}</span>
                                          <a href={documentUrl} target="_blank" rel="noopener noreferrer" className={styles.viewLink}>
                                            View Current Document
                                          </a>
                                        </div>
                                      );
                                    })()}
                                    <input
                                      ref={(el) => {
                                        const inputKey = `document-${panel.id}-${itemIndex}`;
                                        fileInputRefs.current[inputKey] = el;
                                      }}
                                      type="file"
                                      accept=".pdf,.doc,.docx"
                                      onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                          handleDocumentUpload(panel.id, itemIndex, e.target.files[0]);
                                        }
                                      }}
                                      disabled={uploadingDocument}
                                      className={styles.fileInput}
                                    />
                                    {uploadingDocument && <span className={styles.uploading}>Uploading...</span>}
                                  </div>
                                </div>
                              ) : (
                                <div className={styles.formGroup}>
                                  <label>Link URL</label>
                                  <div className={styles.urlGroup}>
                                    <input
                                      type="text"
                                      value={item.link_value || ''}
                                      onChange={(e) => handleItemChange(panel.id, itemIndex, 'link_value', e.target.value)}
                                      className={styles.input}
                                      placeholder="https://..."
                                    />
                                    {item.link_value && (
                                      <a href={item.link_value} target="_blank" rel="noopener noreferrer" className={styles.testLink}>
                                        Test
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* List Text Format */}
                          {panel.format === 'list_text' && (
                            <div className={styles.formGroup}>
                              <label>Text</label>
                              <textarea
                                value={item.text || ''}
                                onChange={(e) => handleItemChange(panel.id, itemIndex, 'text', e.target.value)}
                                className={styles.textarea}
                                rows={3}
                                placeholder="Enter quote or text..."
                              />
                            </div>
                          )}

                          {/* List of Videos Format */}
                          {panel.format === 'list_videos' && (
                            <>
                              <div className={styles.formGroup}>
                                <label>Title</label>
                                <input
                                  type="text"
                                  value={item.title || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'title', e.target.value)}
                                  className={styles.input}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Subtext</label>
                                <textarea
                                  value={item.subtext || ''}
                                  onChange={(e) => handleItemChange(panel.id, itemIndex, 'subtext', e.target.value)}
                                  className={styles.textarea}
                                  rows={2}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>YouTube Video URL</label>
                                <div className={styles.urlGroup}>
                                  <input
                                    type="text"
                                    value={item.youtube_url || ''}
                                    onChange={(e) => handleItemChange(panel.id, itemIndex, 'youtube_url', e.target.value)}
                                    className={styles.input}
                                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                                  />
                                  {item.youtube_url && (
                                    <a href={item.youtube_url} target="_blank" rel="noopener noreferrer" className={styles.testLink}>
                                      Open
                                    </a>
                                  )}
                                </div>
                                <p className={styles.helpText}>Paste a YouTube watch or share link. Videos display as embedded players with an option to expand.</p>
                              </div>
                            </>
                          )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
    </div>
  );
};

export default PanelEditor;

